import { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Step from 'components/global/Step'
import Section from 'components/shared/Section'
import Text from 'components/shared/Text'
import Box from 'components/shared/Box'
import CardSectionDetails from 'routes/unlimited-card/CardSummary/CardSectionDetails'
import UnlimitedCardTypePicker from 'routes/unlimited-card/CardConfiguration/UnlimitedCardConfigurator/UnlimitedCardTypePicker'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useUnlimited } from 'store/unlimited-slice/hooks'
import {
    setCardDetails,
    setCardType,
    setDeliveryMethod,
    setIsCalendarModalOpened,
    setPersonType,
    setRenewCardCurrentData,
} from 'store/unlimited-slice'
import { setScrollId } from 'store/general-slice'
import { useGetCurrentOrderCardQuery } from 'store/api-slice/hooks'
import { useApp } from 'store/steps'
import { useCart } from 'store/cart-slice/hooks'
import { CardPerson } from 'store/api-slice/types'
import { pushDataLayer } from 'helpers/analytics'
import moment from 'moment'

const RenewSummary = () => {
    const { language } = useApp()
    const dispatch = useAppDispatch()
    const { formatMessage } = useIntl()
    const { removeShipping } = useUnlimited()
    const { isCartEmpty } = useCart()

    const { isCalendarModalOpened } = useAppSelector((state) => state.unlimited)
    const { cards } = useAppSelector((state) => state.cart.items)

    const { data: currentOrderData, isFetching: isCurrentOrderFetching } =
        useGetCurrentOrderCardQuery({
            _locale: language,
        })

    const {
        firstName,
        lastName,
        email,
        phoneNumber: phone,
        cardType,
        person,
        dateOfBirth,
        description,
        expiryDateNewCard,
        nextEditionType,
    } = currentOrderData || {}

    const currentOrderDetails = {
        dateOfBirth,
        email,
        firstName,
        phone,
        lastName,
        person,
        cardType,
        personDescription: description,
        expirationDate: moment(expiryDateNewCard).format('DD.MM.YYYY'),
    }

    const isCurrentCardPlus = cardType?.includes('unlimited_plus')

    const handleToggleCalendar = () => {
        dispatch(setIsCalendarModalOpened(!isCalendarModalOpened))
    }

    useEffect(() => {
        if (isCurrentOrderFetching || !currentOrderData) return
        if (!firstName || !lastName || !dateOfBirth || !email || !phone) return

        // TODO: prepare more compact function to pass all the data in one call
        dispatch(
            setPersonType({
                index: 0,
                personDetail: {
                    key: person as CardPerson,
                    title: description,
                },
            })
        )
        dispatch(
            setCardDetails({ field: 'firstName', value: firstName, index: 0 })
        )
        dispatch(
            setCardDetails({ field: 'lastName', value: lastName, index: 0 })
        )
        dispatch(
            setCardDetails({
                field: 'dateOfBirth',
                value: dateOfBirth,
                index: 0,
            })
        )
        dispatch(setCardDetails({ field: 'email', value: email, index: 0 }))
        dispatch(setCardDetails({ field: 'phone', value: phone, index: 0 }))

        if (!isCartEmpty) {
            dispatch(
                setCardType({
                    index: 0,
                    productId: cards[0].productId,
                })
            )
        }

        dispatch(setRenewCardCurrentData(currentOrderData))
    }, [isCurrentOrderFetching])

    useEffect(() => {
        dispatch(setScrollId(''))
        dispatch(setDeliveryMethod('selfPickup'))
        removeShipping()

        // < analytics
        pushDataLayer({
            event: 'productPresented',
            ecommerce: {
                items: [
                    {
                        item_name: 'Unlimited',
                        item_brand: 'PoP',
                    },
                ],
            },
        })
        // analytics >
    }, [])

    return (
        <Step
            title={formatMessage({
                id: 'global.steps.caption_unlimited_renew_summary',
                defaultMessage: 'Twoja nowa karta',
            })}
            isButtonDisabled={isCartEmpty}
            isButtonVisible={true}
        >
            <Section>
                <Box
                    direction="row"
                    isFullWidth
                    justify="space-between"
                    align="center"
                >
                    <Text variant="heading2">
                        <FormattedMessage
                            id="annual.renew.summary_details_header"
                            defaultMessage="Szczegóły karty"
                        />
                    </Text>
                </Box>
                <CardSectionDetails
                    // TODO: try to correct types when more time will be available
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    cardBox={{
                        ...currentOrderDetails,
                    }}
                    isRenewed
                />
            </Section>
            <Section>
                <UnlimitedCardTypePicker
                    index={0}
                    // TODO: try to correct types when more time will be available
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    personType={person}
                    calendarModalToggle={handleToggleCalendar}
                    isRenewed
                    isAlreadyPlus={isCurrentCardPlus}
                    cardEdition={nextEditionType}
                />
            </Section>
        </Step>
    )
}

export default RenewSummary
